import type { ReactNode } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Info, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

/**
 * An (i) that opens a short explanation. Where a page used to open with a
 * paragraph on method and sources, that text lives here instead.
 */
export function InfoTip({ children, label = 'How this works', className }: { children: ReactNode; label?: string; className?: string }) {
  return (
    <Popover className={clsx('relative inline-flex', className)}>
      <PopoverButton
        aria-label={label}
        className="flex h-5 w-5 items-center justify-center rounded-full text-text-muted hover:text-text-secondary focus:outline-none"
      >
        <Info className="h-4 w-4" />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom start"
        className="z-50 mt-1 w-80 rounded-lg border border-border-light bg-surface-primary p-3 text-body-xs leading-relaxed text-text-secondary shadow-medium [--anchor-gap:4px]"
      >
        {children}
      </PopoverPanel>
    </Popover>
  );
}

/**
 * A muted "N data notes" chip for caveats about the numbers (sync gaps, unmapped
 * rows, reconciliation differences). Caveats stay one click away instead of
 * filling the page with yellow banners; real failures still use ErrorState.
 */
export function DataNotes({ notes }: { notes: ReactNode[] }) {
  if (notes.length === 0) return null;
  return (
    <Popover className="relative inline-flex">
      <PopoverButton className="inline-flex items-center gap-1.5 rounded-full border border-warning/30 bg-warning-light/50 px-2.5 py-0.5 text-body-xs font-medium text-text-secondary hover:bg-warning-light focus:outline-none">
        <AlertCircle className="h-3.5 w-3.5 text-warning" aria-hidden />
        {notes.length} data {notes.length === 1 ? 'note' : 'notes'}
      </PopoverButton>
      <PopoverPanel
        anchor="bottom end"
        className="z-50 mt-1 w-96 space-y-2 rounded-lg border border-border-light bg-surface-primary p-3 text-body-xs leading-relaxed text-text-secondary shadow-medium [--anchor-gap:4px]"
      >
        {notes.map((note, i) => (
          <div key={i} className={clsx(i > 0 && 'border-t border-border-light pt-2')}>
            {note}
          </div>
        ))}
      </PopoverPanel>
    </Popover>
  );
}

/** The one grey line under a page title: when the data last synced, and from where. */
export function SyncedAt({ children }: { children: ReactNode }) {
  return <p className="text-body-xs text-text-tertiary">{children}</p>;
}
